import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Calendar, Clock, MapPin, CheckCircle, AlertCircle, 
  Plus, X, UserCheck, Clipboard, HandHeart, Trophy, Coffee,
  Settings, ChevronRight, Info
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TYPE_LABELS = {
  game_day_setup: { label: 'Game Day Setup', icon: Settings, color: 'bg-blue-100 text-blue-700' },
  cleanup: { label: 'Cleanup', icon: Clipboard, color: 'bg-gray-100 text-gray-700' },
  snack_table: { label: 'Snack Stand', icon: Coffee, color: 'bg-orange-100 text-orange-700' },
  chain_crew: { label: 'Chain Crew', icon: Users, color: 'bg-green-100 text-green-700' },
  event_table: { label: 'Event Table', icon: Clipboard, color: 'bg-purple-100 text-purple-700' },
  fundraiser_support: { label: 'Fundraiser', icon: Trophy, color: 'bg-gold/20 text-gold' },
  team_parent_support: { label: 'Team Parent', icon: HandHeart, color: 'bg-pink-100 text-pink-700' },
  equipment_day_support: { label: 'Equipment Day', icon: Settings, color: 'bg-navy/10 text-navy' },
  community_event_support: { label: 'Community Event', icon: Users, color: 'bg-light-blue/20 text-light-blue' },
  registration_table: { label: 'Registration', icon: Clipboard, color: 'bg-teal-100 text-teal-700' },
  photography: { label: 'Photography', icon: Calendar, color: 'bg-indigo-100 text-indigo-700' },
  general: { label: 'General', icon: HandHeart, color: 'bg-gray-100 text-gray-700' }
};

const VolunteerPage = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [myCommitments, setMyCommitments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('opportunities');
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [signupNotes, setSignupNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [oppsRes, commitmentsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/volunteer/opportunities`, { withCredentials: true }),
        axios.get(`${API_URL}/api/volunteer/my-commitments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/volunteer/stats`, { withCredentials: true })
      ]);
      setOpportunities(oppsRes.data);
      setMyCommitments(commitmentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch volunteer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (opportunityId) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/volunteer/signup`, {
        opportunity_id: opportunityId,
        notes: signupNotes
      }, { withCredentials: true });
      setSelectedOpp(null);
      setSignupNotes('');
      fetchData();
    } catch (error) {
      console.error('Failed to sign up:', error);
      alert(error.response?.data?.detail || 'Failed to sign up');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSignup = async (signupId) => {
    if (!window.confirm('Are you sure you want to cancel this volunteer commitment?')) return;
    try {
      await axios.delete(`${API_URL}/api/volunteer/signup/${signupId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to cancel signup:', error);
      alert(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const OpportunityCard = ({ opp, showSignup = true }) => {
    const roleInfo = ROLE_TYPE_LABELS[opp.role_type] || ROLE_TYPE_LABELS.general;
    const RoleIcon = roleInfo.icon;
    const isFull = opp.status === 'full' || opp.available_slots === 0;
    const isSignedUp = opp.user_signed_up;

    return (
      <Card 
        className={`mb-4 ${isFull ? 'opacity-75' : ''} ${isSignedUp ? 'border-green-500 border-2' : ''}`}
        data-testid={`opportunity-card-${opp.opportunity_id}`}
      >
        <CardContent className="pt-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${roleInfo.color}`}>
                <RoleIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-navy">{opp.title}</h3>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                  {opp.event_title && (
                    <Badge
                      variant="outline"
                      className="border-navy text-navy"
                      data-testid={`opp-linked-event-${opp.opportunity_id}`}
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      Linked to: {opp.event_title}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              {isFull ? (
                <Badge variant="secondary" className="bg-gray-200">Full</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-700">
                  {opp.available_slots} spot{opp.available_slots !== 1 ? 's' : ''} left
                </Badge>
              )}
            </div>
          </div>

          {opp.description && (
            <p className="text-gray-600 text-sm mb-3">{opp.description}</p>
          )}

          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-navy" />
              <span>{format(new Date(opp.date), 'EEE, MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-navy" />
              <span>{opp.start_time} - {opp.end_time}</span>
            </div>
            {opp.location && (
              <div className="flex items-center space-x-2 col-span-2">
                <MapPin className="w-4 h-4 text-navy" />
                <span>{opp.location}</span>
              </div>
            )}
            {opp.team_name && (
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4 text-navy" />
                <span>{opp.team_name}</span>
              </div>
            )}
          </div>

          {showSignup && (
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="text-sm text-gray-500">
                {opp.filled_slots}/{opp.num_slots} volunteers
                {typeof opp.spots_remaining === 'number' && (
                  <span className="text-xs text-gray-400 ml-2">
                    ({opp.spots_remaining} left)
                  </span>
                )}
              </div>
              {isSignedUp ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" /> Signed Up
                </Badge>
              ) : !isFull ? (
                <Button
                  size="sm"
                  onClick={() => setSelectedOpp(opp)}
                  className="bg-navy hover:bg-navy/90"
                  data-testid={`signup-btn-${opp.opportunity_id}`}
                >
                  Sign Up
                </Button>
              ) : null}
            </div>
          )}

          {/* Feb 2026 — expandable roster (privacy-safe display names) */}
          {opp.signups && opp.signups.length > 0 && (
            <details className="pt-2 border-t text-sm" data-testid={`roster-${opp.opportunity_id}`}>
              <summary className="cursor-pointer text-navy font-semibold py-1">
                Volunteers ({opp.signup_count ?? opp.signups.length})
              </summary>
              <ul className="mt-2 space-y-1 pl-2">
                {opp.signups.map((s) => (
                  <li key={s.signup_id} className="flex items-center justify-between text-xs">
                    <span>
                      <span className="font-medium">{s.display_name}</span>
                      {s.role_title && (
                        <span className="text-gray-500"> — {s.role_title}</span>
                      )}
                    </span>
                    {s.status && s.status !== 'confirmed' && (
                      <span className="text-gray-400 capitalize">{s.status.replace('_', ' ')}</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </CardContent>
      </Card>
    );
  };

  const CommitmentCard = ({ commitment }) => {
    const opp = commitment.opportunity;
    const roleInfo = ROLE_TYPE_LABELS[opp.role_type] || ROLE_TYPE_LABELS.general;
    const RoleIcon = roleInfo.icon;

    return (
      <Card className="mb-4 border-green-200" data-testid={`commitment-card-${commitment.signup_id}`}>
        <CardContent className="pt-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${roleInfo.color}`}>
                <RoleIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-navy">{opp.title}</h3>
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                  {opp.event_title && (
                    <Badge
                      variant="outline"
                      className="border-navy text-navy"
                      data-testid={`commitment-linked-event-${commitment.signup_id}`}
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      Linked to: {opp.event_title}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Badge className={commitment.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>
              {commitment.status}
            </Badge>
          </div>
          {commitment.role_title && (
            <p className="text-xs text-gray-700 mb-2" data-testid={`commitment-role-${commitment.signup_id}`}>
              Role: <span className="font-medium">{commitment.role_title}</span>
            </p>
          )}

          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-navy" />
              <span>{format(new Date(opp.date), 'EEE, MMM d, yyyy')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-navy" />
              <span>{opp.start_time} - {opp.end_time}</span>
            </div>
            {opp.location && (
              <div className="flex items-center space-x-2 col-span-2">
                <MapPin className="w-4 h-4 text-navy" />
                <span>{opp.location}</span>
              </div>
            )}
          </div>

          {opp.instructions && (
            <div className="bg-light-blue/5 rounded-lg p-3 mb-3">
              <div className="flex items-start space-x-2">
                <Info className="w-4 h-4 text-light-blue mt-0.5" />
                <p className="text-sm text-gray-700">{opp.instructions}</p>
              </div>
            </div>
          )}

          {commitment.status === 'confirmed' && (
            <div className="flex justify-end pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCancelSignup(commitment.signup_id)}
                className="text-red hover:text-red hover:bg-red/5"
                data-testid={`cancel-signup-btn-${commitment.signup_id}`}
              >
                Cancel Signup
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const SignupModal = () => {
    if (!selectedOpp) return null;
    const roleInfo = ROLE_TYPE_LABELS[selectedOpp.role_type] || ROLE_TYPE_LABELS.general;

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-bold text-navy">Volunteer Sign Up</h2>
            <Button variant="ghost" size="sm" onClick={() => setSelectedOpp(null)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="p-4">
            <div className="mb-4">
              <h3 className="font-semibold text-navy mb-2">{selectedOpp.title}</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(selectedOpp.date), 'EEEE, MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>{selectedOpp.start_time} - {selectedOpp.end_time}</span>
                </div>
                {selectedOpp.location && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedOpp.location}</span>
                  </div>
                )}
              </div>
            </div>

            {selectedOpp.instructions && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Instructions:</p>
                <p className="text-sm text-gray-600">{selectedOpp.instructions}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <Textarea
                value={signupNotes}
                onChange={(e) => setSignupNotes(e.target.value)}
                placeholder="Any notes for the organizer..."
                rows={3}
                data-testid="signup-notes-input"
              />
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setSelectedOpp(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSignup(selectedOpp.opportunity_id)}
                disabled={submitting}
                className="flex-1 bg-navy hover:bg-navy/90"
                data-testid="confirm-signup-btn"
              >
                {submitting ? 'Signing up...' : 'Confirm Sign Up'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="volunteer-page">
        {/* Header - Athletic Style */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Make a Difference</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter mb-1">
            Volunteer
          </h1>
          <p className="text-gray-600 font-body">Help make Park City Chiefs great!</p>
        </div>

        {/* Stats - Athletic Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <p className="font-display text-3xl font-black text-navy">{stats.open_opportunities}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Open</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <p className="font-display text-3xl font-black text-light-blue">{stats.unfilled_slots}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">Spots</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <p className="font-display text-3xl font-black text-green-600">{stats.my_commitments}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">My Signups</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
              <p className="font-display text-3xl font-black text-gold">{stats.opportunities_this_week}</p>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-1">This Week</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-2 mb-6 border-b pb-2">
          <Button
            variant={activeTab === 'opportunities' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('opportunities')}
            className={activeTab === 'opportunities' ? 'bg-navy' : ''}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Opportunities ({opportunities.length})
          </Button>
          <Button
            variant={activeTab === 'my-commitments' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('my-commitments')}
            className={activeTab === 'my-commitments' ? 'bg-navy' : ''}
          >
            <UserCheck className="w-4 h-4 mr-2" />
            My Commitments ({myCommitments.length})
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'opportunities' && (
          <div>
            {opportunities.length > 0 ? (
              opportunities.map((opp) => (
                <OpportunityCard key={opp.opportunity_id} opp={opp} />
              ))
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-navy opacity-30" />
                  <h3 className="text-xl font-semibold text-navy mb-2">No Opportunities</h3>
                  <p className="text-gray-600">
                    Check back soon for volunteer opportunities!
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'my-commitments' && (
          <div>
            {myCommitments.length > 0 ? (
              myCommitments.map((commitment) => (
                <CommitmentCard key={commitment.signup_id} commitment={commitment} />
              ))
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <HandHeart className="w-16 h-16 mx-auto mb-4 text-navy opacity-30" />
                  <h3 className="text-xl font-semibold text-navy mb-2">No Commitments Yet</h3>
                  <p className="text-gray-600 mb-4">
                    Sign up for volunteer opportunities to see them here!
                  </p>
                  <Button
                    onClick={() => setActiveTab('opportunities')}
                    className="bg-navy hover:bg-navy/90"
                  >
                    View Opportunities
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Signup Modal */}
        <SignupModal />
      </div>
    </ParentLayout>
  );
};

export default VolunteerPage;
